<script setup lang="ts">
import JPCDiamond from '../design-system/icons/JPCDiamond.vue';

withDefaults(
  defineProps<{
    title?: string;
    showDiamond?: boolean;
    showBanner?: boolean;
    fullPage?: boolean;
  }>(),
  {
    title: null,
    showDiamond: true,
    showBanner: true,
    fullPage: true,
  },
);
const { $t } = useNuxtApp();
const site = useSiteStore();
const pageGradient = computed(() =>
  site.getRegion === 'ZA'
    ? 'bg-[linear-gradient(351deg,_transparent_65.54%,_#005FE8_93.02%)]'
    : 'bg-[linear-gradient(351deg,_transparent_65.54%,_#D839FF_93.02%)]',
);
</script>

<template>
  <div
    class="relative overflow-hidden"
    :class="{ 'w-dvw': fullPage, 'w-full': !fullPage }"
  >
    <div
      :class="[
        'h-full w-[150%] xl:w-full',
        '-translate-x-[15%] xl:translate-x-0 -translate-y-[5%]',
        'rounded-t-[16rem] md:rounded-t-full',
        'opacity-25 flex justify-center items-end absolute',
        pageGradient,
      ]"
    ></div>
    <div
      class="h-full w-full flex flex-col pt-4 justify-start items-center"
      :class="[
        !!$attrs?.['container-classes'] && $attrs['container-classes'],
        {
          'p-4': fullPage,
        },
      ]"
    >
      <section
        class="rounded-xl relative z-5"
        :class="[
          $attrs?.['container-classes'] && $attrs?.['container-classes'],
          {
            'bg-layer-secondary max-w-[31.25rem] w-full': fullPage,
            'w-fit': !fullPage,
          },
        ]"
      >
        <header
          class="aspect-[125/51] h-full w-full rounded-t-xl relative overflow-hidden"
          :class="{
            'dark:bg-dark-700 bg-light-200 flex justify-center items-center':
              !$slots.banner && showBanner,
            hidden: !showBanner,
          }"
        >
          <slot name="banner" />
          <NuxtLink v-if="!$slots.banner && showBanner" to="/">
            <DesignSystemLogo
              element-name="platform-logo"
              image-class="cursor-pointer py-2 w-44"
            />
          </NuxtLink>
          <div
            class="w-full h-1/3 absolute bottom-0 left-0 rounded-t-xl pointer-events-none"
            :class="[
              'dark:bg-[linear-gradient(180deg,_transparent_0%,_#131316_100%)]',
              'bg-[linear-gradient(180deg,_transparent_0%,_#ffffff_100%)]',
            ]"
          />
        </header>
        <div
          class="w-full flex flex-col justify-center items-center rounded-lg text-center"
          :class="{
            'pt-4': !$slots.title && title,
            'pb-4': fullPage,
            'md:pb-0 pb-4': !fullPage,
          }"
        >
          <slot name="title" />
          <p
            v-if="!!title && !$slots.title"
            class="text-base-priority font-bold text-xl"
          >
            {{ $t(title) }}
          </p>
          <slot name="component" />
        </div>
      </section>
      <JPCDiamond
        v-if="showDiamond"
        :class="[
          'animate-pulse !duration-1000 text-transparent',
          {
            'max-w-[500px] min-w-[320px] -translate-y-1/2 md:block hidden':
              !fullPage,
            'max-w-[1000px] min-w-[600px] -translate-y-[45%]': fullPage,
          },
          $attrs?.['diamond-classes'] && $attrs['diamond-classes'],
        ]"
        fetch-priority="high"
        loading="eager"
      />
    </div>
    <slot v-if="$slots['seo-content']" name="seo-content" />
  </div>
</template>
